import { Link } from "react-router-dom";
import { useRef, type ReactNode } from "react";
import {
  BookOpen,
  ChevronRight,
  FileText,
  HelpCircle,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import {
  getKakaoChatUrl,
  isValidKakaoPfUrl,
  openExternalUrl,
} from "../lib/openExternal";

function openKakaoChat() {
  const url = getKakaoChatUrl();

  if (!isValidKakaoPfUrl(url)) {
    alert("카카오톡 채널 주소가 잘못 설정되어 있습니다. 관리자에게 문의해 주세요.");
    return;
  }

  openExternalUrl(url);
}

export default function Support() {
  return (
    <div className="-mt-3 min-h-[100dvh] bg-[#fff4ea] pb-20 pt-3">
      <div className="mx-auto max-w-5xl px-4">
        <section className="relative mt-3 overflow-hidden rounded-[30px] border border-orange-200 bg-gradient-to-r from-[#f97316] via-[#fb923c] to-[#f59e0b] p-6 text-white shadow-[0_24px_60px_rgba(194,65,12,0.22)] sm:p-8">
          <div className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/15" />
          <div className="pointer-events-none absolute -left-12 bottom-[-76px] h-44 w-44 rounded-full bg-white/10" />

          <div className="relative">
            <div className="inline-flex rounded-full bg-white/20 px-3 py-1 text-xs font-semibold tracking-wide">
              고객지원 센터
            </div>

            <h1 className="mt-4 text-3xl font-black leading-tight sm:text-4xl">
              필요한 도움을 빠르게
              <br className="hidden sm:block" />
              찾을 수 있도록 정리했습니다.
            </h1>

            <p className="mt-3 text-sm text-orange-50 sm:text-base">
              FAQ, 이용 가이드, 약관/정책 안내를 통해 대부분의 문의를 바로 해결할 수 있습니다.
            </p>
          </div>
        </section>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to="/contracts/new?type=drink"
            className="inline-flex items-center rounded-full border border-orange-300 bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm transition hover:bg-orange-50"
          >
            결제등록 바로가기
          </Link>
          <Link
            to="/faq"
            className="inline-flex items-center rounded-full border border-orange-300 bg-white px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm transition hover:bg-orange-50"
          >
            FAQ 빠르게 보기
          </Link>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <SupportCard
            title="자주 묻는 질문(FAQ)"
            description="가장 많이 질문하시는 내용을 먼저 확인해보세요."
            to="/faq"
            icon={<HelpCircle className="h-5 w-5 text-orange-600" />}
          />
          <SupportCard
            title="이용 가이드"
            description="결제등록부터 결제까지 순서대로 따라갈 수 있습니다."
            to="/guide"
            icon={<BookOpen className="h-5 w-5 text-orange-600" />}
          />
          <SupportCard
            title="서비스 이용약관"
            description="서비스 이용 조건과 정책을 확인하세요."
            to="/terms"
            icon={<FileText className="h-5 w-5 text-orange-600" />}
          />
          <SupportCard
            title="개인정보 처리방침"
            description="개인정보 수집·이용 및 보호 정책을 확인하세요."
            to="/privacy"
            icon={<ShieldCheck className="h-5 w-5 text-orange-600" />}
          />

          <SupportExternalCard
            title="카카오톡 문의"
            description="주류페이 카카오톡 채널로 바로 연결됩니다."
            onClick={openKakaoChat}
          />
        </div>

        <section className="mt-7 rounded-2xl border border-orange-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-black text-slate-900">운영 안내</h2>

          <div className="mt-3 space-y-2 text-sm text-slate-700">
            <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
              상담 가능 시간: 평일 09:00 ~ 24:00 (연중무휴)
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
              이메일: julyupay@gmail.com
            </div>
            <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3">
              문의는 접수 순서대로 확인 후 답변드립니다.
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

type CardProps = {
  title: string;
  description: string;
  to: string;
  icon: ReactNode;
};

function SupportCard({ title, description, to, icon }: CardProps) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between rounded-2xl border border-orange-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 border border-orange-100">
          {icon}
        </span>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-600">{description}</p>
        </div>
      </div>

      <ChevronRight className="h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-orange-500" />
    </Link>
  );
}

function SupportExternalCard({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  const lastTapRef = useRef(0);

  const handleClick = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 600) return;
    lastTapRef.current = now;
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group sm:col-span-2 rounded-2xl border border-orange-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#fee500] border border-black/10">
            <MessageCircle className="h-5 w-5 text-black" />
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-slate-900">{title}</h3>
            <p className="mt-1 text-sm text-slate-600">{description}</p>
            <p className="mt-2 text-xs font-medium text-orange-600 group-hover:text-orange-700">
              눌러서 카카오톡으로 이동
            </p>
          </div>
        </div>

        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-slate-400 transition group-hover:text-orange-500" />
      </div>
    </button>
  );
}
